/**
 * Slug-hiding browser history for vanity domains.
 *
 * On a vanity host (e.g. `jewishpro.ai` → tenant `m2-team`) the tenant
 * slug must be INVISIBLE in the address bar — the host already implies
 * the tenant. But the entire app (route tree, <TenantBoundary>,
 * `useParams().tenantSlug`, every `buildPath(...)` link) is built around
 * a `/:tenantSlug/*` URL shape. Rewriting all of that would be a large,
 * risky change touching dozens of files.
 *
 * Instead we transform ONLY at the history boundary:
 *   - APP space  (what React Router sees) always carries the slug:
 *     `/m2-team/login`, `/m2-team/chat`, …
 *   - BROWSER space (what the user sees + what links point at) never
 *     does: `/login`, `/chat`, …
 *
 * `readLocation()` prefixes the live browser path into app space for
 * matching; `toBrowser()` strips one leading `/<slug>` before anything
 * is written to `window.history` or rendered into an <a href>. The net
 * effect: every existing route/guard/link keeps working unchanged, and
 * the slug simply never appears in the URL.
 *
 * Inbound URLs are the third leg of that contract. The app's own links
 * are always written slug-less, but URLs arriving from OUTSIDE it carry
 * the slug: password-reset / invite emails are built server-side as
 * `/<slug>/reset-password?token=…`, and people paste `/<slug>/…` links to
 * each other. Those are normalized to slug-less ONCE at construction
 * (see `normalizeBrowserUrl`) so they match. Only an exact leading slug
 * SEGMENT is stripped — a foreign `/other-tenant/login` is left alone,
 * still gets double-prefixed to something unmatchable, and collapses to
 * the tenant home, so this host can never be coaxed into serving another
 * tenant or into showing its own slug.
 *
 * This object implements the minimal surface React Router v6's
 * `unstable_HistoryRouter` consumes from a `history` instance
 * (`history` is not a top-level dependency, so we cannot import it):
 * `action`, `location`, `createHref`, `push`, `replace`, `go`, `listen`.
 */

import type { Location, To, Path } from 'react-router-dom';
import { isPublicLegalPath } from '../modules/legal/publicPaths';
import { isPopupLandingPath } from './popupLandings';

type Action = 'POP' | 'PUSH' | 'REPLACE';
type Listener = (update: { action: Action; location: Location }) => void;

export interface SlugHidingHistory {
  readonly action: Action;
  readonly location: Location;
  createHref(to: To): string;
  push(to: To, state?: unknown): void;
  replace(to: To, state?: unknown): void;
  go(delta: number): void;
  listen(listener: Listener): () => void;
}

/** Per-navigation key (scroll restoration / location identity). App
 *  code may use Math.random — the ban only applies to workflow scripts. */
function newKey(): string {
  return Math.random().toString(36).slice(2, 10);
}

/** Normalize a React Router `To` (string | Partial<Path>) into a Path
 *  with the pathname/search/hash split out. */
function asPath(to: To): Path {
  if (typeof to !== 'string') {
    return { pathname: to.pathname || '/', search: to.search || '', hash: to.hash || '' };
  }
  let pathname = to;
  let hash = '';
  let search = '';
  const h = pathname.indexOf('#');
  if (h >= 0) { hash = pathname.slice(h); pathname = pathname.slice(0, h); }
  const q = pathname.indexOf('?');
  if (q >= 0) { search = pathname.slice(q); pathname = pathname.slice(0, q); }
  return { pathname: pathname || '/', search, hash };
}

export function createSlugHidingHistory(slug: string): SlugHidingHistory {
  const prefix = '/' + slug.toLowerCase();

  // App-space `To` → browser-space URL string. Strips exactly ONE
  // leading `/<slug>`; paths that don't carry the slug (already
  // browser-space, or a foreign `/corpbrain-admin/...` default-login
  // target) pass through unchanged so they remain visibly wrong rather
  // than being silently swallowed.
  const toBrowser = (to: To): string => {
    const p = asPath(to);
    let pathname = p.pathname;
    if (pathname === prefix) pathname = '/';
    else if (pathname.startsWith(prefix + '/')) pathname = pathname.slice(prefix.length);
    return (pathname || '/') + p.search + p.hash;
  };

  // Live browser location → app-space Location (slug prefixed back in).
  // The platform's public documents (/about, /privacy, /terms) are the same
  // on every host and belong to no tenant, so they stay at the root of app
  // space — read as `/<slug>/privacy` they matched nothing and fell through
  // to the tenant's home. Going out, `toBrowser` already leaves them alone.
  // A consent popup's landing page (Google, monday) is tenant-less for the same reason.
  const readLocation = (): Location => {
    const raw = window.location.pathname;
    const st = (window.history.state || {}) as { usr?: unknown; key?: string };
    return {
      pathname: isPublicLegalPath(raw) || isPopupLandingPath(raw) ? raw : raw === '/' ? prefix : prefix + raw,
      search: window.location.search,
      hash: window.location.hash,
      state: st.usr ?? null,
      key: st.key ?? 'default',
    };
  };

  // Collapse an inbound `/<slug>/…` URL to its browser-space form before
  // anything reads it. Routed through `toBrowser` so the strip rule can
  // never drift from the one used on the way out.
  const normalizeBrowserUrl = (): void => {
    const current =
      window.location.pathname + window.location.search + window.location.hash;
    const normalized = toBrowser(current);
    if (normalized !== current) {
      window.history.replaceState(window.history.state, '', normalized);
    }
  };
  normalizeBrowserUrl();

  let action: Action = 'POP';
  // Cached so the `location` getter returns a STABLE reference between
  // navigations — HistoryRouter snapshots it into useState, and an
  // ever-new object would defeat that.
  let location: Location = readLocation();
  let listeners: Listener[] = [];

  const onPopState = (): void => {
    action = 'POP';
    location = readLocation();
    for (const l of listeners) l({ action, location });
  };

  const commit = (next: Action): void => {
    action = next;
    location = readLocation();
    for (const l of listeners) l({ action, location });
  };

  return {
    get action() { return action; },
    get location() { return location; },
    createHref(to) { return toBrowser(to); },
    push(to, state) {
      window.history.pushState({ usr: state ?? null, key: newKey() }, '', toBrowser(to));
      commit('PUSH');
    },
    replace(to, state) {
      window.history.replaceState({ usr: state ?? null, key: newKey() }, '', toBrowser(to));
      commit('REPLACE');
    },
    go(delta) { window.history.go(delta); },
    listen(listener) {
      listeners.push(listener);
      // Tie the raw popstate subscription to the presence of listeners
      // so it's cleaned up with them (no leak under StrictMode remounts).
      if (listeners.length === 1) window.addEventListener('popstate', onPopState);
      return () => {
        listeners = listeners.filter((l) => l !== listener);
        if (listeners.length === 0) window.removeEventListener('popstate', onPopState);
      };
    },
  };
}
